import { Button, Drawer } from "@components";
import { useState } from "react";
import styles from "styles/pages/reports/reports.module.scss";
import { EngagementBehaviorChart } from "./EngagementBehaviorChart";
import { Trend } from "pages/Reports/Reports";
import { ReportsAPI } from "providers/ReportsAPI";
import dayjs from "dayjs";

export default function EngagementBehavior({ data = {}, isCashback }) {
  const [selectedData, setSelectedData] = useState(null);
  const [loadingHistoric, setLoadingHistoric] = useState(false);

  const fetchGroupHistory = async (payload) => {
    setLoadingHistoric(true);
    const provider = new ReportsAPI();
    const results = await provider.loyaltyEngagementsHistory(
      "behavior",
      payload.year
    );

    const limit = 12;

    const rows = Array(limit).fill(null);

    if (!results || !results.length) {
      setLoadingHistoric(false);
      return rows;
    }

    for (const result of results) {
      if (!result?.month) continue;

      const format = "MM-YYYY";
      const date = dayjs(result.month, format);
      const index = date.month() + 1;

      if (index >= 1 && index <= limit) {
        rows[index - 1] = Object.fromEntries(
          Object.entries(result)
            .map(([entryKey, obj]) => {
              if (entryKey === "month") return null;
              return [entryKey, Number(obj?.value ?? 0)];
            })
            .filter(Boolean)
        );
      }
    }

    setLoadingHistoric(false);
    return rows;
  };

  const buildTable = () => {
    const statuses = {
      never_received: isCashback
        ? "Nunca ganhou cashback"
        : "Nunca ganhou pontos",
      never_used: isCashback
        ? "Ganhou cashback e nunca usou"
        : "Ganhou pontos e nunca usou",
      used_more_than_a_time: isCashback
        ? "Usou ao menos 1 vez"
        : "Usou pontos ao menos 1 vez",
      used_more_than_three_times: isCashback
        ? "Usou ao menos 3 vezes"
        : "Usou pontos ao menos 3 vezes",
      used_more_than_ten_times: isCashback
        ? "Usou ao menos 10 vezes"
        : "Usou pontos ao menos 10 vezes",
    };

    const rows = [
      "never_received",
      "never_used",
      "used_more_than_a_time",
      "used_more_than_three_times",
      "used_more_than_ten_times",
    ];

    const columns = ["recency", "frequency", "ticket", "orders", "total"];

    const table = rows.map((row) => {
      return {
        alias: row,
        status: statuses[row],
        values: columns.map((column) => {
          const rawValue = data?.[row]?.[column] ?? 0;

          return {
            label: column,
            tuple: `${row}__${column}`,
            value: rawValue,
          };
        }),
      };
    });

    return table;
  };

  return (
    <div className={styles.box2}>
      <div className="d-flex align-center justify-between px-20">
        <div className="d-flex align-center gap-5">
          <p className={styles.label}>
            Comportamento dos grupos de engajamento
          </p>
          <span
            className={styles.info}
            title="Compare recência, frequência, valor, quantidade de pedidos e
              faturamento de cada grupo de engajamento."
          >
            <i className="fal fa-info-circle" />
          </span>
        </div>
        <Button
          design="default-icon-mini-3"
          onClick={() =>
            setSelectedData({
              name: "Comportamento dos grupos de engajamento",
            })
          }
        >
          <i className="fas fa-chart-waterfall" />
        </Button>
      </div>

      <div className={styles.hairline} />

      <div className={styles.tableFull}>
        <table className="w-100">
          <thead>
            <tr>
              <th>GRUPO</th>
              <th>ÚLTIMO PEDIDO HÁ</th>
              <th>INTERVALO ENTRE COMPRAS</th>
              <th>TICKET MÉDIO</th>
              <th>PEDIDOS P/CLIENTE</th>
              <th>FATURAMENTO MÉDIO</th>
            </tr>
          </thead>
          <tbody>
            {buildTable().map((row) => (
              <tr key={row.status}>
                <td className={styles.status} style={{ minWidth: 200 }}>
                  {row.status}
                </td>
                {row.values.map(({ tuple, label, value }, idx) => {
                  return (
                    <td key={idx}>
                      <div className="d-flex align-center gap-5 justify-between">
                        <span>{value.value ?? "---"}</span>
                        <Trend growth={value.growth} />
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Drawer
        visible={selectedData}
        onClose={() => setSelectedData(null)}
        withoutTransition
        direction="full-center"
        showCloseButton={false}
      >
        <EngagementBehaviorChart
          selectedData={selectedData}
          onFetchData={(payload) => fetchGroupHistory(payload)}
          loading={loadingHistoric}
          onClose={() => setSelectedData(false)}
          isCashback={isCashback}
        />
      </Drawer>
    </div>
  );
}
